import { NgIf, NgIfContext } from '@angular/common';
import { ChangeDetectorRef, Directive, inject, Injectable, Input, OnDestroy, TemplateRef, ViewContainerRef } from '@angular/core';
import { NavigationCancel, NavigationEnd, NavigationError, ResolveEnd, Router } from '@angular/router';
import { App } from '@df/core/app';
import { Client } from '@df/core/service/client';
import { Events } from '@df/core/service/event';
import { CdService } from '@df/ng/cd.service';
import { TimeoutService } from '@df/ng/timeout.service';
import { BodyClassService } from '@df/ui/body-class.service';
import { onInViewChangeEvent } from '@df/ui/common/on-in-view.directive';
import { UseService } from '@df/util/service/use.service';
import { BehaviorSubject, Subscription } from 'rxjs';
/**
 * Use this directive to render element only in browser app and after body content is loaded
 *
 * ```html
 * <div *afterContentInit>...</div>
 * ```
 */

@Directive({
    selector: '[afterContentInit]',
    standalone: true
})
export class AfterContentInitDirective extends NgIf implements OnDestroy {
    @Input()
    afterContentInit?: 'router' | '';

    private subscription?: Subscription;

    constructor(
        _viewContainer: ViewContainerRef,
        templateRef: TemplateRef<NgIfContext<unknown>>,
        afterContentInitDirectiveService: AfterContentInitDirectiveService,
        client: Client,
        app: App,
        bodyClassService: BodyClassService,
        changeDetectorRef: ChangeDetectorRef,
        cdService: CdService
    ) {
        super(_viewContainer, templateRef);

        // the second test is for cms mode BUT its for the old one only
        if (client.isCms || ['prerender', 'legacyPrerender', 'ssr'].includes(client.mode)) {
            this.ngIf = true;
        } else if (app.isPlatformBrowser) {
            this.subscription = afterContentInitDirectiveService.loaded.subscribe(value => {
                if (value) {
                    bodyClassService.add('content-initiated', this);
                    this.ngIf = true;
                    if (!this.afterContentInit) {
                        this.subscription?.unsubscribe();
                        this.subscription = undefined;
                    }
                    cdService.push(changeDetectorRef);
                } else if (this.afterContentInit === 'router') {
                    this.ngIf = false;
                    cdService.push(changeDetectorRef);
                }
            });
        }
    }

    ngOnDestroy() {
        this.subscription?.unsubscribe();
    }
}

const CONTEN_INIT_WAIT = 'content_init_wait';

@Injectable({
    providedIn: 'root'
})
export class AfterContentInitDirectiveService implements OnDestroy {
    loaded = new BehaviorSubject(false);
    ready = new BehaviorSubject(false);

    protected subscriptions = new Subscription();

    protected events = inject(Events);

    constructor(
        private app: App,
        private useService: UseService,
        router: Router,
        private timeoutService: TimeoutService
    ) {
        this.lock(this);

        this.subscriptions.add(
            this.loaded.subscribe(() => {
                this.events.broadcast(onInViewChangeEvent);
            })
        );

        this.subscriptions.add(
            router.events.subscribe(event => {
                if (event instanceof ResolveEnd) {
                    if (this.loaded.value) {
                        this.lock(this);
                    }
                } else if (event instanceof NavigationError || event instanceof NavigationCancel || event instanceof NavigationEnd) {
                    if (!this.loaded.value) {
                        this.timeoutService.setTimeout(() => this.done(), 500);
                    }
                }
            })
        );

        this.subscriptions.add(
            this.useService.getState(CONTEN_INIT_WAIT).obs.subscribe(value => {
                this.ready.next(!value);
                if (this.app.isPlatformBrowser) {
                    this.loaded.next(!value);
                }
            })
        );

        if (!this.app.isPlatformBrowser) {
            this.timeoutService.setTimeout(() => {
                const locks = Array.from(this.useService.getState(CONTEN_INIT_WAIT).locks.values()).map(i =>
                    typeof i === 'object' ? i.constructor.name : i
                );
                if (locks?.length) {
                    console.log('Locks left after 50s processing', locks);
                }
            }, 50000);
        }
    }

    ngOnDestroy() {
        this.subscriptions.unsubscribe();
    }

    lock(source: unknown) {
        return this.useService.lock(CONTEN_INIT_WAIT, source);
    }

    unlock(source: unknown) {
        return this.useService.unlock(CONTEN_INIT_WAIT, source);
    }

    private done() {
        this.unlock(this);
    }
}
